/**
 * Moderation Reports Page
 * Displays moderation statistics, charts, and export functionality
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/stores/auth';
import { AdminLayout } from '@/components/layouts/admin-layout';
import { DataExporter } from '@/lib/utils/data-export';
import { navigateBackWithFallback } from '@/lib/utils/navigation';
import { showToast } from '@/lib/utils/toast';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import {
  Shield,
  CheckCircle,
  XCircle,
  Flag,
  Clock,
  RefreshCw,
  Download,
  TrendingUp,
  TrendingDown,
  Users,
  MessageCircle,
  Calendar,
  FileText,
  BarChart3,
  PieChartIcon,
  Activity,
  ArrowLeft,
} from 'lucide-react';

// Types for moderation reports
interface ModerationReportSummary {
  totalActions: number;
  byActionType: {
    approve: number;
    reject: number;
    delete: number;
    flag: number;
    hide: number;
  };
  byModerator: Array<{
    id: number;
    name: string;
    count: number;
    avatar?: string;
  }>;
  averageResponseTime: string;
  periodComparison: {
    current: number;
    previous: number;
    percentChange: number;
  };
}

interface TimelineDataPoint {
  date: string;
  approve: number;
  reject: number;
  delete: number;
  flag: number;
  total: number;
}

interface RecentAction {
  id: number;
  action: string;
  target: string;
  moderator: string;
  timestamp: string;
  reason?: string;
}

interface ModerationReportsResponse {
  summary: ModerationReportSummary;
  timeline: TimelineDataPoint[];
  recentActions: RecentAction[];
}

interface BackendModerationReportsResponse {
  summary: {
    totalActions: number;
    byActionType?: Partial<Record<'approve' | 'reject' | 'delete' | 'flag' | 'hide', number>>;
    byModerator?: Array<{
      id: number;
      name: string;
      count: number;
      avatar?: string;
    }>;
    averageResponseTime: string;
    periodComparison?: {
      current: number;
      previous: number;
      percentChange: number;
    };
  };
  timeline: Array<{
    date: string;
    approve?: number;
    reject?: number;
    delete?: number;
    flag?: number;
    hide?: number;
    total?: number;
    count?: number;
  }>;
  recentActions: Array<{
    id: number;
    action?: string;
    actionType?: string;
    target?: string;
    content?: string;
    moderator?: string | { id: number; name: string } | null;
    timestamp?: string;
    moderatedAt?: string;
    reason?: string;
    stream?: { id: number; title: string } | null;
  }>;
}

// Chart configuration
const actionChartConfig = {
  approve: {
    label: 'Approved',
    color: 'hsl(142, 76%, 36%)',
  },
  reject: {
    label: 'Rejected',
    color: 'hsl(0, 84%, 60%)',
  },
  delete: {
    label: 'Deleted',
    color: 'hsl(0, 72%, 51%)',
  },
  flag: {
    label: 'Flagged',
    color: 'hsl(38, 92%, 50%)',
  },
  hide: {
    label: 'Hidden',
    color: 'hsl(217, 91%, 60%)',
  },
  total: {
    label: 'Total',
    color: 'hsl(262, 83%, 58%)',
  },
} satisfies ChartConfig;

const normalizeReportsResponse = (data: BackendModerationReportsResponse): ModerationReportsResponse => {
  const byActionType = {
    approve: Number(data.summary.byActionType?.approve || 0),
    reject: Number(data.summary.byActionType?.reject || 0),
    delete: Number(data.summary.byActionType?.delete || 0),
    flag: Number(data.summary.byActionType?.flag || 0),
    hide: Number(data.summary.byActionType?.hide || 0),
  };

  const summary: ModerationReportSummary = {
    totalActions: Number(data.summary.totalActions || 0),
    byActionType,
    byModerator: (data.summary.byModerator || []).map((moderator) => ({
      id: moderator.id,
      name: moderator.name,
      count: Number(moderator.count || 0),
      avatar: moderator.avatar,
    })),
    averageResponseTime: data.summary.averageResponseTime || '0m',
    periodComparison: data.summary.periodComparison || {
      current: Number(data.summary.totalActions || 0),
      previous: Number(data.summary.totalActions || 0),
      percentChange: 0,
    },
  };

  const timeline: TimelineDataPoint[] = (data.timeline || []).map((item) => {
    const approve = Number(item.approve || 0);
    const reject = Number(item.reject || 0);
    const deleted = Number(item.delete || 0);
    const flag = Number(item.flag || 0);
    const total = Number(item.total ?? item.count ?? (approve + reject + deleted + flag));

    return {
      date: item.date,
      approve,
      reject,
      delete: deleted,
      flag,
      total,
    };
  });

  const recentActions: RecentAction[] = (data.recentActions || []).map((action) => {
    const actionType = action.action || action.actionType || 'flag';
    const target = action.target
      || action.content
      || action.stream?.title
      || 'Moderated content';
    const moderator = typeof action.moderator === 'string'
      ? action.moderator
      : action.moderator?.name || 'Unknown moderator';
    const timestamp = action.timestamp || action.moderatedAt || new Date().toISOString();

    return {
      id: action.id,
      action: actionType,
      target,
      moderator,
      timestamp,
      reason: action.reason,
    };
  });

  return {
    summary,
    timeline,
    recentActions,
  };
};

export default function ModerationReportsPage() {
  const navigate = useNavigate();
  const { isInitialized } = useAuthStore();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ModerationReportSummary | null>(null);
  const [timeline, setTimeline] = useState<TimelineDataPoint[]>([]);
  const [recentActions, setRecentActions] = useState<RecentAction[]>([]);
  const [exporting, setExporting] = useState(false);

  // Filter state
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d');

  // Fetch moderation reports
  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query params
      const params = new URLSearchParams();
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      switch (dateRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }
      params.append('startDate', startDate.toISOString());
      params.append('endDate', endDate.toISOString());

      const response = await api.get<BackendModerationReportsResponse>(`/api/moderator/reports?${params.toString()}`);

      if (response.success && response.data) {
        const normalized = normalizeReportsResponse(response.data);
        setSummary(normalized.summary);
        setTimeline(normalized.timeline);
        setRecentActions(normalized.recentActions);
      } else {
        setError(response.error?.message || 'Failed to load moderation reports');
        setSummary(null);
        setTimeline([]);
        setRecentActions([]);
      }
    } catch (err) {
      console.error('Error fetching moderation reports:', err);
      setError('Failed to load moderation reports');
      setSummary(null);
      setTimeline([]);
      setRecentActions([]);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Initial fetch
  useEffect(() => {
    if (isInitialized) {
      fetchReports();
    }
  }, [isInitialized, fetchReports]);

  // Prepare pie chart data
  const pieChartData = useMemo(() => {
    if (!summary) return [];
    return [
      { name: 'Approved', value: summary.byActionType.approve, color: '#22c55e' },
      { name: 'Rejected', value: summary.byActionType.reject, color: '#ef4444' },
      { name: 'Deleted', value: summary.byActionType.delete, color: '#dc2626' },
      { name: 'Flagged', value: summary.byActionType.flag, color: '#f59e0b' },
      { name: 'Hidden', value: summary.byActionType.hide, color: '#3b82f6' },
    ].filter(item => item.value > 0);
  }, [summary]);

  // Export handlers
  const handleExportCSV = async () => {
    if (!timeline.length) {
      showToast.warning('No data to export');
      return;
    }

    setExporting(true);
    try {
      const exportData = timeline.map(item => ({
        Date: item.date,
        Approved: item.approve,
        Rejected: item.reject,
        Deleted: item.delete,
        Flagged: item.flag,
        Total: item.total,
      }));

      await DataExporter.exportData(exportData, {
        filename: `moderation_reports_${dateRange}_${new Date().toISOString().split('T')[0]}`,
        format: 'csv',
      });
    } catch (err) {
      console.error('Export failed:', err);
      showToast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = async () => {
    if (!summary || !timeline.length) {
      showToast.warning('No data to export');
      return;
    }

    setExporting(true);
    try {
      const exportData = {
        summary,
        timeline,
        recentActions,
        exportedAt: new Date().toISOString(),
        dateRange,
      };

      await DataExporter.exportData([exportData], {
        filename: `moderation_reports_full_${dateRange}_${new Date().toISOString().split('T')[0]}`,
        format: 'json',
      });
    } catch (err) {
      console.error('Export failed:', err);
      showToast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Format helpers
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now.getTime() - time.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const getActionBadge = (action: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      approve: 'default',
      reject: 'destructive',
      delete: 'destructive',
      flag: 'secondary',
      hide: 'outline',
    };
    const colors: Record<string, string> = {
      approve: 'bg-green-100 text-green-800 border-green-200',
      reject: 'bg-red-100 text-red-800 border-red-200',
      delete: 'bg-red-100 text-red-800 border-red-200',
      flag: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      hide: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return (
      <Badge variant={variants[action] || 'secondary'} className={`capitalize ${colors[action] || ''}`}>
        {action}
      </Badge>
    );
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigateBackWithFallback(navigate, '/admin')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchReports} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Select value={dateRange} onValueChange={(v: '7d' | '30d' | '90d') => setDateRange(v)}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Activity className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Actions</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{summary.totalActions.toLocaleString()}</p>
                    {summary.periodComparison.percentChange !== 0 && (
                      <Badge variant={summary.periodComparison.percentChange > 0 ? 'default' : 'secondary'} className="text-xs">
                        {summary.periodComparison.percentChange > 0 ? (
                          <TrendingUp className="h-3 w-3 mr-1" />
                        ) : (
                          <TrendingDown className="h-3 w-3 mr-1" />
                        )}
                        {Math.abs(summary.periodComparison.percentChange).toFixed(1)}%
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Approved</p>
                  <p className="text-2xl font-bold">{summary.byActionType.approve.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Rejected</p>
                  <p className="text-2xl font-bold">{summary.byActionType.reject.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Flag className="h-4 w-4 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Flagged</p>
                  <p className="text-2xl font-bold">{summary.byActionType.flag.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Avg Response</p>
                  <p className="text-2xl font-bold">{summary.averageResponseTime}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Section */}
      <Tabs defaultValue="timeline" className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="timeline">
              <BarChart3 className="h-4 w-4 mr-2" />
              Timeline
            </TabsTrigger>
            <TabsTrigger value="distribution">
              <PieChartIcon className="h-4 w-4 mr-2" />
              Distribution
            </TabsTrigger>
            <TabsTrigger value="moderators">
              <Users className="h-4 w-4 mr-2" />
              Moderators
            </TabsTrigger>
            <TabsTrigger value="recent">
              <MessageCircle className="h-4 w-4 mr-2" />
              Recent
            </TabsTrigger>
          </TabsList>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={exporting}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportJSON} disabled={exporting}>
              <FileText className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
          </div>
        </div>

        {/* Timeline Chart */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle>Moderation Activity Over Time</CardTitle>
              <CardDescription>
                Daily breakdown of moderation actions for the selected period
              </CardDescription>
            </CardHeader>
            <CardContent>
              {timeline.length > 0 ? (
                <ChartContainer config={actionChartConfig} className="h-[400px] w-full">
                  <AreaChart data={timeline}>
                    <defs>
                      <linearGradient id="fillApprove" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="fillReject" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickLine={false}
                      axisLine={false}
                      tickMargin={8}
                      minTickGap={32}
                      tickFormatter={formatDate}
                    />
                    <YAxis tickLine={false} axisLine={false} tickMargin={8} />
                    <ChartTooltip
                      cursor={false}
                      content={
                        <ChartTooltipContent
                          labelFormatter={(value) => formatDate(value)}
                          indicator="dot"
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="approve"
                      stroke="hsl(var(--chart-1))"
                      fill="url(#fillApprove)"
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="reject"
                      stroke="hsl(var(--destructive))"
                      fill="url(#fillReject)"
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="delete"
                      stroke="hsl(var(--destructive))"
                      fill="hsl(var(--destructive))"
                      fillOpacity={0.3}
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="flag"
                      stroke="hsl(var(--warning))"
                      fill="hsl(var(--warning))"
                      fillOpacity={0.3}
                      stackId="1"
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke="hsl(262, 83%, 58%)"
                      fill="hsl(262, 83%, 58%)"
                      fillOpacity={0.15}
                    />
                  </AreaChart>
                </ChartContainer>
              ) : (
                <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                  No data available for the selected period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Chart */}
        <TabsContent value="distribution">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Action Distribution</CardTitle>
                <CardDescription>
                  Breakdown of moderation actions by type
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pieChartData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <ChartTooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-background border rounded-lg p-2 shadow-lg">
                                  <p className="font-medium">{payload[0].name}</p>
                                  <p className="text-muted-foreground">
                                    {payload[0].value?.toLocaleString()} actions
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Action Breakdown</CardTitle>
                <CardDescription>
                  Detailed counts for each action type
                </CardDescription>
              </CardHeader>
              <CardContent>
                {summary && (
                  <div className="space-y-4">
                    {Object.entries(summary.byActionType).map(([action, count]) => {
                      const total = summary.totalActions;
                      const percentage = total > 0 ? (count / total) * 100 : 0;
                      const colors: Record<string, string> = {
                        approve: 'bg-green-500',
                        reject: 'bg-red-500',
                        delete: 'bg-red-600',
                        flag: 'bg-yellow-500',
                        hide: 'bg-blue-500',
                      };
                      return (
                        <div key={action} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="capitalize font-medium">{action}</span>
                            <span className="text-muted-foreground">
                              {count.toLocaleString()} ({percentage.toFixed(1)}%)
                            </span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full ${colors[action] || 'bg-gray-500'} transition-all`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Moderators Tab */}
        <TabsContent value="moderators">
          <Card>
            <CardHeader>
              <CardTitle>Top Moderators</CardTitle>
              <CardDescription>
                Moderators ranked by number of actions taken
              </CardDescription>
            </CardHeader>
            <CardContent>
              {summary && summary.byModerator.length > 0 ? (
                <div className="space-y-4">
                  <ChartContainer config={actionChartConfig} className="h-[300px] w-full">
                    <BarChart data={summary.byModerator} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tickLine={false} axisLine={false} />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tickLine={false}
                        axisLine={false}
                        width={120}
                      />
                      <ChartTooltip
                        cursor={false}
                        content={
                          <ChartTooltipContent
                            labelFormatter={(value) => value}
                            indicator="dot"
                          />
                        }
                      />
                      <Bar dataKey="count" fill="hsl(262, 83%, 58%)" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ChartContainer>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Moderator Details</h4>
                    <div className="space-y-3">
                      {summary.byModerator.map((mod, index) => (
                        <div key={mod.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{mod.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {mod.count.toLocaleString()} actions
                              </p>
                            </div>
                          </div>
                          <Badge variant="secondary">
                            {((mod.count / summary.totalActions) * 100).toFixed(1)}%
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No moderator data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent Actions Tab */}
        <TabsContent value="recent">
          <Card>
            <CardHeader>
              <CardTitle>Recent Moderation Actions</CardTitle>
              <CardDescription>
                Latest moderation activities across all moderators
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recentActions.length > 0 ? (
                <div className="space-y-4">
                  {recentActions.map((action) => (
                    <div key={action.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div className="flex items-start gap-4">
                        <div className="mt-1">
                          {getActionBadge(action.action)}
                        </div>
                        <div>
                          <p className="font-medium">{action.target}</p>
                          <p className="text-sm text-muted-foreground">
                            by {action.moderator}
                          </p>
                          {action.reason && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Reason: {action.reason}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatTimeAgo(action.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-muted-foreground">
                  <div className="text-center">
                    <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No recent actions to display</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Error State */}
      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-destructive" />
                <p className="text-destructive">{error}</p>
              </div>
              <Button variant="outline" onClick={fetchReports} disabled={loading}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
}
